import { useEffect, useReducer, type ReactNode } from 'react';
import type { Client } from '@optimizely/optimizely-sdk';
import {
  createOptimizelyDecisionNotificationHandler,
  createOptimizelyDecisionErrorHandler,
  extractUserGroupFromDeviceId,
  type Track,
} from '@checkatrade/web-third-parties';
import { track } from 'lib/sdks/analytics';
import { useSession } from 'lib/auth/auth.hooks';
import { getDeviceId } from 'lib/utils/device';
import { debugLog } from 'lib/utils/debug';
import { setOptimizelyUserForTracking } from 'lib/sdks/analytics/plugins/optimizely';
import type { OptimizelyDatafile } from 'lib/sdks/analytics/plugins/optimizely';
import { useDispatch } from 'react-redux';
import { setInitialDecisions } from './utils';
import useIsMobileScreen from '@hooks/use-is-mobile-screen';

import {
  OptimizelyContext,
  OptimizelyDispatchContext,
  optimizelyReducer,
  initialState,
  SET_CLIENT,
  SET_USER,
} from './optimizely.reducer';

export * from './optimizely.hook';

declare global {
  interface Window {
    optimizelyDatafile?: OptimizelyDatafile;
    optimizelySdk?: any;
    optimizelyInstance: Client;
  }
}

const errorHandler = createOptimizelyDecisionErrorHandler(
  track as unknown as Track,
);

export const OptimizelyProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatchToContext] = useReducer(
    optimizelyReducer,
    initialState,
  );
  const isMobile = useIsMobileScreen();
  const session = useSession();
  const dispatchToStore = useDispatch();

  const errorHandlerWithFailSafe = (reason?: string) => {
    errorHandler.trackError(reason);

    dispatchToContext({
      type: SET_USER,
      user: null,
    });
  };

  useEffect(() => {
    if (window.optimizelyDatafile && window.optimizelySdk) {
      const optimizelyInstance = window.optimizelySdk.createInstance({
        // https://docs.developers.optimizely.com/feature-experimentation/docs/initialize-sdk-javascript#parameters
        datafile: window.optimizelyDatafile,
        errorHandler,
        logger: { log: debugLog.bind(this, 'Optimizely logger, level:') },
        // we don't have this product so disable, is enabled by default https://docs.developers.optimizely.com/feature-experimentation/docs/initialize-sdk-javascript#odpmanager
        odpOptions: {
          disabled: true,
        },
      }) as Client;
      optimizelyInstance
        .onReady({ timeout: 5000 }) // https://docs.developers.optimizely.com/feature-experimentation/docs/initialize-sdk-javascript#onready-details
        .then(({ success, reason }) => {
          if (success) {
            return dispatchToContext({
              type: SET_CLIENT,
              client: optimizelyInstance,
            });
          }

          // eslint-disable-next-line no-console
          console.warn(`client initialization unsuccessful, reason: ${reason}`); // Note: This should be handled by Datadog when it is installed
          errorHandlerWithFailSafe(reason);
        })
        .catch(error => errorHandlerWithFailSafe(error.message));
    } else {
      errorHandlerWithFailSafe(
        'optimizelyDatafile or optimizelySdk not loaded',
      );
    }
  }, []);

  useEffect(() => {
    if (state.client) {
      const deviceId = getDeviceId();
      const loggedInUserId = session.data?.user.id ?? null;

      const loggedUser = state.client.createUserContext(deviceId, {
        loggedInUserId,
        loggedIn: Boolean(loggedInUserId),
        isNewUser: session.data?.user.isNewUser ?? null,
        deviceId,
        isMobile,
        userGroup: extractUserGroupFromDeviceId(deviceId),
      });

      const initialDecisions = loggedUser?.decideAll([
        window.optimizelySdk.OptimizelyDecideOption.DISABLE_DECISION_EVENT,
      ]);

      if (loggedUser?.trackEvent && window.optimizelyDatafile) {
        setOptimizelyUserForTracking(loggedUser, window.optimizelyDatafile);
      }

      state.client?.notificationCenter?.addNotificationListener(
        window.optimizelySdk?.enums?.NOTIFICATION_TYPES.DECISION,
        createOptimizelyDecisionNotificationHandler(track as unknown as Track),
      );

      if (initialDecisions)
        dispatchToStore(setInitialDecisions(initialDecisions));
      dispatchToContext({ type: SET_USER, user: loggedUser });
    }
  }, [state.client, session.data, isMobile]);

  return (
    <OptimizelyContext.Provider value={state}>
      <OptimizelyDispatchContext.Provider value={dispatchToContext}>
        {children}
      </OptimizelyDispatchContext.Provider>
    </OptimizelyContext.Provider>
  );
};
